import { Temporal } from '@js-temporal/polyfill';

const weekdays = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const monthKeys = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
export const month = ['jan(?:uary)?','feb(?:ruary)?','mar(?:ch)?','apr(?:il)?','may','june?','july?','aug(?:ust)?','sept?(?:ember)?',
  'oct(?:ober)?','nov(?:ember)?','dec(?:ember)?'].join('|');
/** Short month abbreviations end in a period that does not end a sentence. */
export const monthAbbreviation = /\b(?:jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)$/i;

/**
 * One date phrase in a reply, resolved against the calendar date the reply was received.
 * `date` is null when the phrase is ambiguous (`alternatives` lists the readings) or names no single day.
 */
export interface DateMention { phrase: string; index: number; date: string | null; alternatives: string[]; past: boolean }

/** "Fri, Oct 9": how a resolved date is shown to a person. */
export function shortDate(date: string): string {
  const day = Temporal.PlainDate.from(date);
  return `${weekdays[day.dayOfWeek - 1]!.slice(0,1).toUpperCase()}${weekdays[day.dayOfWeek - 1]!.slice(1,3)}, `
    + `${monthKeys[day.month - 1]!.slice(0,1).toUpperCase()}${monthKeys[day.month - 1]!.slice(1)} ${day.day}`;
}

export function monthIndex(name: string) { return monthKeys.indexOf(name.slice(0,3).toLowerCase()) + 1; }

export function dateMentions(text: string, receivedOn: string): DateMention[] {
  const base = Temporal.PlainDate.from(receivedOn); const lower = text.toLowerCase();
  const latest = base.add({ years:1 }); const found: DateMention[] = []; const taken: [number, number][] = [];
  const free = (start: number, end: number) => !taken.some(([a, b]) => start < b && end > a);
  type Reading = { date: Temporal.PlainDate | null; alternatives?: Temporal.PlainDate[]; past?: boolean };
  const push = (match: RegExpMatchArray, resolve: () => Reading) => {
    const index = match.index!; const end = index + match[0].length;
    if (!free(index, end)) return; taken.push([index, end]);
    let result: ReturnType<typeof resolve>;
    try { result = resolve(); } catch { result = { date:null }; }
    const date = result.date && Temporal.PlainDate.compare(result.date, latest) <= 0 ? result.date : null;
    const past = result.past ?? (!!date && Temporal.PlainDate.compare(date, base) < 0);
    const alternatives = (result.alternatives ?? []).map(String);
    found.push({ phrase:match[0].trim(), index, date:date?.toString() ?? null, alternatives, past });
  };
  /** A month and day without a year: a recent past day stays past (a paid claim), an older one means next year. */
  const yearless = (monthNumber: number, day: number) => {
    const date = Temporal.PlainDate.from({ year:base.year, month:monthNumber, day }, { overflow:'reject' });
    if (Temporal.PlainDate.compare(date, base) >= 0 || base.since(date).days <= 31) return { date };
    return { date:Temporal.PlainDate.from({ year:base.year + 1, month:monthNumber, day }, { overflow:'reject' }) };
  };
  for (const match of lower.matchAll(/\b\d{4}-\d{2}-\d{2}\b/g)) {
    push(match, () => ({ date:Temporal.PlainDate.from(match[0], { overflow:'reject' }) }));
  }
  const dayMonth = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${month})\\b\\.?(?:,?\\s+(\\d{4})\\b)?`, 'g');
  const monthDay = new RegExp(`\\b(${month})\\b\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b(?:,?\\s+(\\d{4})\\b)?`, 'g');
  for (const [pattern, monthGroup, dayGroup] of [[monthDay, 1, 2], [dayMonth, 2, 1]] as const) {
    for (const match of lower.matchAll(pattern)) push(match, () => {
      const monthNumber = monthIndex(match[monthGroup]!); const day = Number(match[dayGroup]);
      if (!match[3]) return yearless(monthNumber, day);
      return { date:Temporal.PlainDate.from({ year:Number(match[3]), month:monthNumber, day }, { overflow:'reject' }) };
    });
  }
  const endOf = new RegExp(`\\b(?:end of (?:the |this )?month|month[- ]end|eom|end of next month|end of (${month}))\\b`, 'g');
  for (const match of lower.matchAll(endOf)) push(match, () => {
    if (match[0].includes('next month')) { const next = base.add({ months:1 }); return { date:next.with({ day:next.daysInMonth }) }; }
    if (!match[1]) return { date:base.with({ day:base.daysInMonth }) };
    const start = yearless(monthIndex(match[1]), 1).date; return { date:start.with({ day:start.daysInMonth }) };
  });
  const weekday = new RegExp(String.raw`\b(?:(next|this|last)\s+)?(${weekdays.join('|')})\b`
    + String.raw`(?:\s+(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)?\b)?`, 'g');
  for (const match of lower.matchAll(weekday)) push(match, () => {
    const day = weekdays.indexOf(match[2]!) + 1; const today = base.dayOfWeek;
    if (match[3]) {
      let candidate = base.with({ day:Number(match[3]) }, { overflow:'reject' });
      if (Temporal.PlainDate.compare(candidate, base) < 0) {
        candidate = base.add({ months:1 }).with({ day:Number(match[3]) }, { overflow:'reject' });
      }
      return { date:candidate.dayOfWeek === day ? candidate : null };
    }
    if (match[1] === 'last') return { date:base.subtract({ days:(today - day + 7) % 7 || 7 }), past:true };
    if (match[1] === 'this') return { date:base.add({ days:day - today }) };
    if (match[1] === 'next') {
      const following = base.add({ days:7 - today + day });
      // Said early in the week, "next Friday" can mean this week's Friday or the one after: ask.
      return day > today ? { date:null, alternatives:[base.add({ days:day - today }), following] } : { date:following };
    }
    const ahead = (day - today + 7) % 7;
    return ahead === 0 ? { date:null, alternatives:[base, base.add({ days:7 })] } : { date:base.add({ days:ahead }) };
  });
  for (const match of lower.matchAll(/\b(?:tomorrow|today|yesterday|last week|next week|this week)\b/g)) push(match, () => {
    if (match[0] === 'tomorrow') return { date:base.add({ days:1 }) };
    if (match[0] === 'today') return { date:base };
    if (match[0] === 'yesterday') return { date:base.subtract({ days:1 }) };
    return { date:null, past:match[0] === 'last week' };
  });
  return found.sort((a, b) => a.index - b.index);
}

/** The single upcoming date a reply names, or null when it names none, several, or an ambiguous one. */
export function parseReplyDate(text: string, receivedOn: string): string | null {
  const upcoming = dateMentions(text, receivedOn).filter(mention => !mention.past);
  if (upcoming.some(mention => !mention.date)) return null;
  const values = new Set(upcoming.map(mention => mention.date!));
  return values.size === 1 ? [...values][0]! : null;
}
